import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  ArrowLeft,
  Video,
  Calendar,
  Clock,
  Users,
  UserCheck,
  UserX,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react'

interface WorkshopInfo {
  id: string
  title: string
  scheduled_date: string
  duration_minutes: number
  teacher_id: string | null
  cohort_id: string | null
  unit_title: string
  unit_order: number
  teacher_name: string
  teacher_email: string
  cohort_name: string
}

interface AttendanceRow {
  type: 'teacher' | 'student'
  name: string
  email: string
  attended_at: string | null
  delay_display: string
  delay_minutes: number | null
  status: 'attended' | 'absent'
}

function calculateDelay(scheduledDate: string, attendedAt: string | null): {
  delay_display: string
  delay_minutes: number | null
} {
  if (!attendedAt) {
    return { delay_display: 'No asistio', delay_minutes: null }
  }
  const scheduled = new Date(scheduledDate).getTime()
  const attended = new Date(attendedAt).getTime()
  const diffMinutes = Math.round((attended - scheduled) / 60000)

  if (diffMinutes <= 0) {
    return { delay_display: 'A tiempo', delay_minutes: 0 }
  }
  return { delay_display: `+${diffMinutes} min`, delay_minutes: diffMinutes }
}
async function fetchTeacherInfo(teacherId: string) {
  const { data: tData } = await (supabase.from('teachers') as any)
    .select('id, profile:profiles!profile_id(first_name, last_name, email)')
    .eq('id', teacherId).single()
  if (!tData?.profile) return { name: 'Sin profesor', email: '' }
  return {
    name: `${tData.profile.first_name} ${tData.profile.last_name}`,
    email: tData.profile.email || ''
  }
}

async function fetchCohortStudentIds(teacherId: string): Promise<string[]> {
  const { data: tRecord } = await (supabase.from('teachers') as any)
    .select('profile_id').eq('id', teacherId).single()
  if (!tRecord?.profile_id) return []
  const { data: teacherCohorts } = await (supabase.from('cohorts') as any)
    .select('student_ids').eq('teacher_id', tRecord.profile_id).eq('is_active', true)
  return [...new Set((teacherCohorts || []).flatMap((c: any) => c.student_ids || []))] as string[]
}

async function fetchCohortInfo(cohortId: string, teacherId: string | null) {
  const { data: cData } = await (supabase.from('cohorts') as any)
    .select('name, student_ids').eq('id', cohortId).single()
  const studentIds: string[] = cData?.student_ids || []
  const name = cData?.name || 'Sin cohorte'

  if (studentIds.length === 0 && teacherId) {
    const fallbackIds = await fetchCohortStudentIds(teacherId)
    return { name, studentIds: fallbackIds }
  }
  return { name, studentIds }
}

async function buildStudentRows(
  workshopId: string,
  scheduledDate: string,
  cohortStudentIds: string[]
): Promise<{ rows: AttendanceRow[], attendanceByStudentId: Record<string, any> }> {
  const { data: sAttData } = await (supabase.from('workshop_attendance') as any)
    .select(`id, student_id, status, attended_at,
      student:students!student_id(id, profile_id,
        profile:profiles!profile_id(first_name, last_name, email))`)
    .eq('workshop_id', workshopId)

  const attendanceByStudentId: Record<string, any> = {}
  for (const a of (sAttData || [])) {
    attendanceByStudentId[a.student_id] = a
  }

  const rows: AttendanceRow[] = []
  const addedStudentIds = new Set<string>()

  if (cohortStudentIds.length > 0) {
    const { data: cohortStudents } = await (supabase.from('students') as any)
      .select(`id, profile_id, profile:profiles!profile_id(first_name, last_name, email)`)
      .in('profile_id', cohortStudentIds)
      .order('created_at', { ascending: true })

    for (const student of (cohortStudents || [])) {
      addedStudentIds.add(student.id)
      const att = attendanceByStudentId[student.id]
      const delay = calculateDelay(scheduledDate, att?.attended_at || null)
      const profile = student.profile as any
      rows.push({
        type: 'student',
        name: `${profile?.first_name || ''} ${profile?.last_name || ''}`,
        email: profile?.email || '',
        attended_at: att?.attended_at || null,
        delay_display: delay.delay_display,
        delay_minutes: delay.delay_minutes,
        status: att?.status === 'attended' ? 'attended' : 'absent'
      })
    }
  }

  for (const att of (sAttData || [])) {
    if (addedStudentIds.has(att.student_id)) continue
    addedStudentIds.add(att.student_id)
    const profile = (att.student as any)?.profile
    const delay = calculateDelay(scheduledDate, att.attended_at)
    rows.push({
      type: 'student',
      name: `${profile?.first_name || ''} ${profile?.last_name || ''}`,
      email: profile?.email || '',
      attended_at: att.attended_at || null,
      delay_display: delay.delay_display,
      delay_minutes: delay.delay_minutes,
      status: att.status === 'attended' ? 'attended' : 'absent'
    })
  }

  return { rows, attendanceByStudentId }
}
  
export default function AttendanceDetail() {
  const { workshopId } = useParams()
  const [workshop, setWorkshop] = useState<WorkshopInfo | null>(null)
  const [rows, setRows] = useState<AttendanceRow[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (workshopId) loadData()
  }, [workshopId])

  const loadData = async () => {
  try {
    const { data: wData, error: wErr } = await (supabase.from('unit_workshops') as any)
      .select(`id, title, scheduled_date, duration_minutes, teacher_id, cohort_id,
        unit:units!unit_id(order_num, title)`)
      .eq('id', workshopId as string).single()

    if (wErr) { console.error('Error loading workshop:', wErr); return }

    const teacher = wData.teacher_id
      ? await fetchTeacherInfo(wData.teacher_id)
      : { name: 'Sin profesor', email: '' }

    const cohort = wData.cohort_id
      ? await fetchCohortInfo(wData.cohort_id, wData.teacher_id)
      : { name: 'Sin cohorte', studentIds: [] }

    setWorkshop({
      id: wData.id, title: wData.title || '',
      scheduled_date: wData.scheduled_date,
      duration_minutes: wData.duration_minutes,
      teacher_id: wData.teacher_id, cohort_id: wData.cohort_id,
      unit_title: wData.unit?.title || '', unit_order: wData.unit?.order_num ?? 0,
      teacher_name: teacher.name, teacher_email: teacher.email,
      cohort_name: cohort.name
    })

    const allRows: AttendanceRow[] = []

    if (wData.teacher_id) {
      const { data: tAtt } = await (supabase.from('teacher_workshop_attendance') as any)
        .select('status, attended_at')
        .eq('workshop_id', workshopId as string)
        .eq('teacher_id', wData.teacher_id)
      const tRecord = tAtt?.[0]
      const delay = calculateDelay(wData.scheduled_date, tRecord?.attended_at || null)
      allRows.push({
        type: 'teacher', name: teacher.name, email: teacher.email,
        attended_at: tRecord?.attended_at || null,
        delay_display: delay.delay_display, delay_minutes: delay.delay_minutes,
        status: tRecord?.status === 'attended' ? 'attended' : 'absent'
      })
    }

    const { rows: studentRows } = await buildStudentRows(
      workshopId as string, wData.scheduled_date, cohort.studentIds
    )
    allRows.push(...studentRows)

    allRows.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'teacher' ? -1 : 1
      return a.name.localeCompare(b.name)
    })

    setRows(allRows)
  } catch (err) {
    console.error('Error loading attendance detail:', err)
  } finally {
    setIsLoading(false)
  }
}

  const stats = {
    total: rows.length,
    attended: rows.filter(r => r.status === 'attended').length,
    absent: rows.filter(r => r.status === 'absent').length
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!workshop) {
    return (
      <div className="text-center py-12">
        <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Taller no encontrado</p>
        <Link to="/admin/attendance" className="text-[#0055A4] hover:underline text-sm mt-2 inline-block">
          Volver a asistencia
        </Link>
      </div>
    )
  }

  const scheduledTime = new Date(workshop.scheduled_date).toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit'
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/admin/attendance"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Detalle de Asistencia</h1>
          <p className="text-gray-600">{workshop.title}</p>
        </div>
      </div>

      {/* Workshop Info */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-indigo-100">
            <Video className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">{workshop.title}</h2>
            <p className="text-sm text-gray-500 mb-4">
              Unidad {workshop.unit_order}: {workshop.unit_title} | Cohorte: {workshop.cohort_name}
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(workshop.scheduled_date).toLocaleDateString('es-CL', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{workshop.duration_minutes} minutos</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>{rows.length} participantes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#0055A4]/10">
              <Users className="w-5 h-5 text-[#0055A4]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total Personas</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.attended}</p>
              <p className="text-sm text-gray-500">Asistieron</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <UserX className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
              <p className="text-sm text-gray-500">No Asistieron</p>
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Hora Programada</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Hora de Entrada</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Atraso</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.length > 0 ? (
                rows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      {row.type === 'teacher' ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          Profesor
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#0055A4]/10 text-[#0055A4]">
                          Alumno
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{row.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.email}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">{scheduledTime}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">
                      {row.attended_at
                        ? new Date(row.attended_at).toLocaleTimeString('es-CL', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {row.delay_minutes === null ? (
                        <span className="text-sm text-gray-400">{row.delay_display}</span>
                      ) : row.delay_minutes === 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {row.delay_display}
                        </span>
                      ) : row.delay_minutes <= 5 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          {row.delay_display}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {row.delay_display}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {row.status === 'attended' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Asistio
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <XCircle className="w-3.5 h-3.5" />
                          No asistio
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No hay registros de asistencia</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
